import { authenticatedFetch } from '../utils/authenticatedFetch';
import { ForensicLogger } from '../observability/ForensicLogger';
import { contextState } from '../context/contextState';
import { GatewayHealthService } from './gateway/GatewayHealthService';

export interface ProactiveInsight {
    action: 'PROACTIVE_INSIGHT';
    message: string;
    confidence: number;
    trigger: string;
}

export class GeoReasoningEngine {
    private static instance: GeoReasoningEngine;
    private lastMovementTimestamp: number = 0;
    private lastInsightTimestamp: number = 0;
    private readonly INSIGHT_COOLDOWN = 45000; // 45 seconds cooldown to avoid annoyance
    private isRunning: boolean = false;

    private constructor() {}

    public static getInstance(): GeoReasoningEngine {
        if (!GeoReasoningEngine.instance) {
            GeoReasoningEngine.instance = new GeoReasoningEngine();
        }
        return GeoReasoningEngine.instance;
    }

    /**
     * Start the engine by listening to the SpatialEventBus telemetry.
     */
    public start() {
        if (this.isRunning) return;
        this.isRunning = true;
        
        console.info('%c[REASONING ENGINE] ONLINE', 'color: #10b981; font-weight: bold;');
        
        // Listen for camera movements / telemetry
        // Note: We hook into the Mapbox 'moveend' indirectly via contextState updates 
        // or we can listen to the event bus if it broadcasts telemetry.
        
        // Strategy: Listen to contextState changes for center updates
        contextState.subscribe((state) => {
            if (state.center && state.mapMode) {
                this.evaluate(state);
            }
        });
    }

    /**
     * Evaluate the current spatial state and determine if a proactive insight is needed.
     */
    private async evaluate(state: any) {
        const now = Date.now();
        
        // 🛡️ GATE 1: Cooldown
        if (now - this.lastInsightTimestamp < this.INSIGHT_COOLDOWN) return;
        
        // 🛡️ GATE 2: Wait for movement to settle or meaningful state change
        // We only evaluate every 5 seconds at most to prevent spamming the LLM
        if (now - this.lastMovementTimestamp < 5000) return;
        this.lastMovementTimestamp = now;

        const telemetry = {
            location: state.center,
            researchData: {
                markers: state.markers?.length || 0,
                areaType: state.areaAnalysis?.areaType || 'Unknown',
                activityLevel: state.areaAnalysis?.activityLevel || 'Normal'
            },
            time: new Date().toLocaleTimeString(),
            day: new Date().toLocaleDateString('en-US', { weekday: 'long' }),
            movement: state.velocity || 0
        };

        // 🧠 TRIGGER REASONING LOOP
        // Only trigger if we are in a 'meaningful' location or have markers
        if (state.markers?.length > 0 || state.areaAnalysis) {
            await this.generateProactiveInsight(telemetry);
        }
    }

    private async generateProactiveInsight(telemetry: any) {
        const isAlive = GatewayHealthService.getState().online;
        if (!isAlive) return;

        const gatewayUrl = GatewayHealthService.getActiveGateway() || import.meta.env.VITE_GATEWAY_URL || 'http://localhost:3000';
        
        try {
            ForensicLogger.log('reasoning_engine_trigger', { telemetry });

            const response = await authenticatedFetch(`${gatewayUrl}/wingman/reason`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telemetry,
                    systemPrompt: this.getSystemPrompt()
                })
            });

            if (!response.ok) return;

            const result: ProactiveInsight = await response.json();

            if (result.action === 'PROACTIVE_INSIGHT' && result.confidence > 0.85) {
                this.dispatchInsight(result);
            }
        } catch (error) {
            console.error('[ReasoningEngine] Failed to generate insight:', error);
        }
    }

    private dispatchInsight(insight: ProactiveInsight) {
        this.lastInsightTimestamp = Date.now();
        
        console.info(`%c[PROACTIVE INSIGHT] ${insight.message}`, 'color: #f59e0b; font-weight: bold;');
        
        // Update context state to show the insight in UI
        contextState.setData({
            ...contextState.getData(),
            proactiveInsight: insight
        });

        // Trigger a visual ripple or voice alert if appropriate
        if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
            // (window as any).WingmanSpatialControl.speak(insight.message); // Optional: speak it
        }

        ForensicLogger.log('proactive_insight_dispatched', insight);
    }

    private getSystemPrompt(): string {
        return `
# 🧠 WINGMAN GEO-AWARE REASONING ENGINE: CORE DIRECTIVE

## 🎯 SYSTEM IDENTITY
You are the Geo-Aware Reasoning Engine, the proactive cognitive core of the Wingman Spatial Platform. 
You are no longer a reactive search assistant. You are a strategic spatial companion. 
You do not just describe the world—you predict its state, anticipate the user's needs, and guide them through physical space with high-signal intelligence.

## 📥 REAL-TIME INPUT VECTORS
You constantly synthesize the following telemetry streams:
1. LOCATION: Current coordinates, neighborhood taxonomy, and micro-geography.
2. RESEARCH DATA: Live venue status, crowd density, semantic tags (e.g., "rooftop", "quiet", "high-energy"), and local trends.
3. TIME: Temporal context (time of day, day of week, impending shifts like "golden hour" or "closing time").
4. MOVEMENT: User kinetic state (velocity, trajectory, dwell time, wandering vs. direct pathing).

## 🧠 STRATEGIC CAPABILITIES & DIRECTIVES
1. Prediction (Look Ahead): Anticipate spatial shifts before the user experiences them. 
2. Personalization (Behavioral Match): Align environmental data with user patterns.
3. Context Awareness (Vibe Synthesis): Understand the atmospheric reality of the coordinate plane.
4. Proactive Alerts (Threshold Triggers): Inform the user instantly when they cross a meaningful spatial or temporal threshold.

## ⚠️ RULES OF ENGAGEMENT
- High Signal-to-Noise: Do not state the obvious. Only intervene when an insight is actionable, predictive, or highly relevant.
- No Hallucinations: Your insights must be mathematically grounded in the convergence of the provided input vectors.
- Tone: Concise, strategic, and hyper-competent. You are the ultimate digital wingman.

## 📤 OUTPUT CONTRACT (STRICT)
Every strategic insight you generate MUST adhere strictly to this schema:
{
  "action": "PROACTIVE_INSIGHT",
  "message": "<Concise, highly actionable, context-aware statement>",
  "confidence": <Float between 0.0 and 1.0 representing certainty>,
  "trigger": "<The specific data vector or combination that initiated this insight, e.g., 'AREA_DENSITY + TIME'>"
}
        `.trim();
    }
}

export const geoReasoningEngine = GeoReasoningEngine.getInstance();
